import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api/client.js';
import { Icon } from '../../components/ui/Icon.js';
import { Switch } from '../../components/ui/Form.js';
import type { Trunk } from '@arcnet/contracts';

/**
 * Everything a carrier dictates.
 *
 * The trunk record has held these fields since the schema was written; nothing
 * could change them. A trunk could be created and not configured, which is the
 * worst of both — it looks provisioned and fails on the first call.
 *
 * Grouped by the question being answered rather than by database column, and
 * the number formatting section carries a live preview because it is the
 * setting that most often breaks a carrier integration and the least possible
 * to reason about from the field names alone.
 */

const CODECS = ['PCMA', 'PCMU', 'G722', 'G729', 'OPUS'];

export function TrunkEditor({ trunk, onDone }: { trunk: Trunk; onDone: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: trunk.name,
    host: trunk.host,
    port: trunk.port,
    transport: trunk.transport,
    use_srv: trunk.use_srv,
    direction: trunk.direction,
    status: trunk.status,
    outbound_format: trunk.outbound_format,
    outbound_prefix: trunk.outbound_prefix ?? '',
    inbound_strip: trunk.inbound_strip,
    inbound_prepend: trunk.inbound_prepend ?? '',
    cli_mode: trunk.cli_mode,
    fallback_cli: trunk.fallback_cli ?? '',
    privacy_mode: trunk.privacy_mode,
    allowed_codecs: trunk.allowed_codecs ?? [],
    dtmf_mode: trunk.dtmf_mode,
    t38_enabled: trunk.t38_enabled,
    early_media: trunk.early_media,
    session_timers: trunk.session_timers,
    rel100: trunk.rel100,
    max_concurrent_calls: trunk.max_concurrent_calls,
    max_cps: trunk.max_cps,
    options_ping_sec: trunk.options_ping_sec,
    failure_threshold: trunk.failure_threshold,
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) =>
    setF((prev) => ({ ...prev, [k]: v }));

  const [tested, setTested] = useState<Array<{ input: string; output: string; note: string | null }> | null>(null);
  const [testing, setTesting] = useState(false);

  const save = useMutation({
    mutationFn: () => api.patch(`/v1/trunks/${trunk.id}`, {
      ...f,
      outbound_prefix: f.outbound_prefix || null,
      inbound_prepend: f.inbound_prepend || null,
      fallback_cli: f.fallback_cli || null,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trunks'] });
      onDone();
    },
  });

  /**
   * Tests what is SAVED, not what is on screen.
   *
   * The endpoint reads the stored trunk, so a preview of unsaved edits would
   * be a lie. Saying so is better than quietly showing the old answer next to
   * new inputs.
   */
  const runTest = async (): Promise<void> => {
    setTesting(true);
    try {
      const r = await api.post<{ results: Array<{ input: string; output: string; note: string | null }> }>(
        `/v1/trunks/${trunk.id}/actions/test-number-format`,
        {
          numbers: ['+27821234567', '+27115550100', '0821234567', '+441632960961', '0800112233'],
          direction: 'outbound',
        },
      );
      setTested(r.results);
    } finally { setTesting(false); }
  };

  const err = save.error instanceof ApiClientError ? save.error : null;

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <h3 className="arc-h2">{trunk.name}</h3>
        <span className="arc-subtle">{trunk.carrier_name}</span>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-sm" onClick={onDone}>
          <Icon name="close" size={12} /> Close
        </button>
      </div>

      {err && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} /><span>{err.message}</span>
        </div>
      )}

      <Section title="Connection" hint="Where the carrier is and how we reach it">
        <Field label="Name">
          <input className="arc-input" value={f.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Host">
          <input className="arc-input arc-mono" value={f.host} onChange={(e) => set('host', e.target.value)} />
        </Field>
        <Field label="Port">
          <input className="arc-input arc-mono" inputMode="numeric" value={f.port}
            onChange={(e) => set('port', Number(e.target.value) || 5060)} />
        </Field>
        <Field label="Transport">
          <select className="arc-input" value={f.transport}
            onChange={(e) => set('transport', e.target.value as typeof f.transport)}>
            <option value="udp">UDP</option><option value="tcp">TCP</option><option value="tls">TLS</option>
          </select>
        </Field>
        <Field label="Direction">
          <select className="arc-input" value={f.direction}
            onChange={(e) => set('direction', e.target.value as typeof f.direction)}>
            <option value="both">Both</option>
            <option value="outbound">Outbound only</option>
            <option value="inbound">Inbound only</option>
          </select>
        </Field>
        <Field label="Status">
          <select className="arc-input" value={f.status}
            onChange={(e) => set('status', e.target.value as typeof f.status)}>
            <option value="active">Active</option>
            {/* Draining stops NEW calls without dropping the ones in progress,
                which is what you want before maintenance. */}
            <option value="draining">Draining</option>
            <option value="disabled">Disabled</option>
          </select>
        </Field>
        <Toggle id="tr-srv" label="Resolve via SRV" checked={f.use_srv}
          onChange={(v) => set('use_srv', v)}
          hint="Let DNS choose the host and port." />
      </Section>

      <Section title="Number formatting"
        hint="What the carrier expects to see — the setting that most often breaks an integration">
        <Field label="Outbound format">
          <select className="arc-input" value={f.outbound_format}
            onChange={(e) => set('outbound_format', e.target.value as typeof f.outbound_format)}>
            <option value="e164">+27821234567 (E.164 with +)</option>
            <option value="e164_no_plus">27821234567 (E.164, no +)</option>
            <option value="national">0821234567 (national)</option>
            <option value="00_prefixed">0027821234567 (00 prefixed)</option>
          </select>
        </Field>
        <Field label="Outbound prefix">
          <input className="arc-input arc-mono" value={f.outbound_prefix}
            placeholder="none" onChange={(e) => set('outbound_prefix', e.target.value)} />
        </Field>
        <Field label="Inbound strip">
          <input className="arc-input arc-mono" inputMode="numeric" value={f.inbound_strip}
            onChange={(e) => set('inbound_strip', Number(e.target.value) || 0)} />
        </Field>
        <Field label="Inbound prepend">
          <input className="arc-input arc-mono" value={f.inbound_prepend}
            placeholder="none" onChange={(e) => set('inbound_prepend', e.target.value)} />
        </Field>
      </Section>

      <div className="arc-card" style={{ marginBottom: 16, background: 'var(--arc-surface-2)' }}>
        <div className="arc-card-head">
          <h2 className="arc-h2">What the carrier will actually receive</h2>
          <button type="button" className="arc-btn arc-btn-sm" disabled={testing}
            onClick={() => void runTest()}>
            {testing ? 'Testing…' : 'Test formatting'}
          </button>
        </div>
        <div className="arc-card-body">
          {!tested ? (
            <p className="arc-muted" style={{ fontSize: 12.5, margin: 0 }}>
              Runs against the <strong>saved</strong> settings, not the ones above — save first,
              then test. A wrong format fails at the carrier with an opaque 404, which
              is miserable to debug from SIP traces alone.
            </p>
          ) : (
            <table className="arc-table">
              <thead><tr><th>Dialled</th><th>Sent</th><th>Note</th></tr></thead>
              <tbody>
                {tested.map((r) => (
                  <tr key={r.input}>
                    <td className="arc-mono">{r.input}</td>
                    <td className="arc-mono" style={{ fontWeight: 600 }}>{r.output}</td>
                    <td className="arc-muted">{r.note ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Section title="Caller ID" hint="How the calling party is presented and withheld">
        <Field label="Presentation">
          <select className="arc-input" value={f.cli_mode}
            onChange={(e) => set('cli_mode', e.target.value as typeof f.cli_mode)}>
            <option value="from_and_pai">From and P-Asserted-Identity</option>
            <option value="from_only">From only</option>
            <option value="pai_only">P-Asserted-Identity only</option>
            <option value="rpid">Remote-Party-ID</option>
          </select>
        </Field>
        <Field label="Fallback CLI">
          <input className="arc-input arc-mono" value={f.fallback_cli}
            placeholder="+27115550100" onChange={(e) => set('fallback_cli', e.target.value)} />
        </Field>
        <Field label="Withholding">
          <select className="arc-input" value={f.privacy_mode}
            onChange={(e) => set('privacy_mode', e.target.value as typeof f.privacy_mode)}>
            <option value="none">Never withhold</option>
            <option value="id">Privacy: id</option>
            <option value="header">Privacy: header</option>
            <option value="full">Privacy: full</option>
          </select>
        </Field>
      </Section>

      <Section title="Media and protocol" hint="What the two ends have to agree on">
        <div className="arc-field" style={{ gridColumn: '1 / -1', margin: 0 }}>
          <label className="arc-label">Codecs offered, in order</label>
          <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {CODECS.map((codec) => {
              const on = f.allowed_codecs.includes(codec);
              return (
                <button key={codec} type="button"
                  className={`arc-btn arc-btn-sm ${on ? 'arc-btn-primary' : 'arc-btn-ghost'}`}
                  onClick={() => set('allowed_codecs', on
                    ? f.allowed_codecs.filter((x) => x !== codec)
                    : [...f.allowed_codecs, codec])}>
                  {codec}
                </button>
              );
            })}
          </div>
          {/* Order is the offer order, and an empty list is not "any codec" --
              it is a trunk that cannot negotiate media at all. */}
          <p className={f.allowed_codecs.length === 0 ? 'arc-error' : 'arc-hint'}>
            {f.allowed_codecs.length === 0
              ? 'No codecs offered — calls on this trunk cannot negotiate media.'
              : `Offered in this order: ${f.allowed_codecs.join(', ')}`}
          </p>
        </div>
        <Field label="DTMF">
          <select className="arc-input" value={f.dtmf_mode}
            onChange={(e) => set('dtmf_mode', e.target.value as typeof f.dtmf_mode)}>
            <option value="rfc2833">RFC 2833 (out of band)</option>
            <option value="info">SIP INFO</option>
            <option value="inband">In band</option>
          </select>
        </Field>
        <Toggle id="tr-t38" label="T.38 fax" checked={f.t38_enabled}
          onChange={(v) => set('t38_enabled', v)} />
        <Toggle id="tr-em" label="Early media" checked={f.early_media}
          onChange={(v) => set('early_media', v)}
          hint="Ringing and network messages before answer." />
        <Toggle id="tr-st" label="Session timers" checked={f.session_timers}
          onChange={(v) => set('session_timers', v)}
          hint="Periodic re-INVITE so a dead call does not bill forever." />
        <Toggle id="tr-100" label="PRACK (100rel)" checked={f.rel100}
          onChange={(v) => set('rel100', v)} />
      </Section>

      <Section title="Capacity and health" hint="What we will send, and when we stop">
        <Field label="Max concurrent calls">
          <input className="arc-input" inputMode="numeric" value={f.max_concurrent_calls ?? ''}
            placeholder="unlimited"
            onChange={(e) => set('max_concurrent_calls', e.target.value ? Number(e.target.value) : null)} />
        </Field>
        <Field label="Max calls per second">
          <input className="arc-input" inputMode="numeric" value={f.max_cps ?? ''}
            placeholder="unlimited"
            onChange={(e) => set('max_cps', e.target.value ? Number(e.target.value) : null)} />
        </Field>
        <Field label="OPTIONS ping (seconds)">
          <input className="arc-input" inputMode="numeric" value={f.options_ping_sec}
            onChange={(e) => set('options_ping_sec', Number(e.target.value) || 0)} />
          <p className="arc-hint">0 disables probing. Some carriers object to it.</p>
        </Field>
        <Field label="Failures before unhealthy">
          <input className="arc-input" inputMode="numeric" value={f.failure_threshold}
            onChange={(e) => set('failure_threshold', Number(e.target.value) || 1)} />
        </Field>
      </Section>

      <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
        {save.isPending ? 'Saving…' : 'Save trunk'}
      </button>
    </form>
  );
}

function Section({ title, hint, children }: {
  title: string; hint: string; children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ marginBottom: 9 }}>
        <h3 className="arc-h2">{title}</h3>
        <p className="arc-muted" style={{ fontSize: 11.5, margin: '2px 0 0' }}>{hint}</p>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12,
      }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="arc-field" style={{ margin: 0 }}>
      <label className="arc-label">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ id, label, hint, checked, onChange }: {
  id: string; label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="arc-row" style={{ gap: 10, alignItems: 'flex-start', paddingTop: 18 }}>
      <Switch id={id} checked={checked} onChange={onChange} />
      <label htmlFor={id} style={{ fontSize: 12.5, cursor: 'pointer' }}>
        {label}
        {hint && (
          <span className="arc-muted" style={{ display: 'block', fontSize: 11, marginTop: 2 }}>
            {hint}
          </span>
        )}
      </label>
    </div>
  );
}
